import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { formatPhone, normalizePhone } from "../../shared/phone.ts";
import type { Me } from "../../shared/types.ts";
import { ApiError, api } from "../lib/api.ts";
import { useI18n } from "../lib/i18n.tsx";
import { storedInvite, useMe } from "../lib/me.tsx";

type Step = "phone" | "otp";

function errorKey(e: unknown): Parameters<ReturnType<typeof useI18n>["t"]>[0] {
  if (!(e instanceof ApiError)) return "error.network";
  switch (e.body.error) {
    case "validation":
      return "error.validation";
    case "invalid_code":
      return "error.invalid_code";
    case "code_expired":
      return "error.code_expired";
    case "too_many_attempts":
      return "error.too_many_attempts";
    case "phone_unavailable":
      return "error.phone_blocked";
    case "rate_limited":
    case "cooldown":
      return "error.rate_limited";
    case "handle_taken":
      return "error.handle_taken";
    case "reserved_handle":
      return "error.reserved_handle";
    case "terms_outdated":
      return "legal.reaccept";
    case "banned":
      return "auth.state.banned";
    default:
      return "error.generic";
  }
}

export function Login() {
  const { t, locale } = useI18n();
  const { me, loading, setMe } = useMe();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next =
    params.get("next") && params.get("next")!.startsWith("/") ? params.get("next")! : "/";

  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const [displayName, setDisplayName] = useState("");
  const [handle, setHandle] = useState("");
  const [agreed, setAgreed] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);

  // Onboarding is derived from the session, never from local step state.
  const showOnboard = !loading && me !== null && me.needs_onboarding;

  useEffect(() => {
    if (!loading && me && !me.needs_onboarding) navigate(next, { replace: true });
  }, [me, loading, navigate, next]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  useEffect(() => {
    if (step === "otp") codeRef.current?.focus();
  }, [step]);

  async function sendCode(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ ok: true; resend_in: number }>("/api/auth/otp", { body: { phone } });
      setResendIn(r.resend_in);
      setCode("");
      setStep("otp");
    } catch (err) {
      if (err instanceof ApiError && err.body.retry_after) setResendIn(err.body.retry_after);
      setError(t(errorKey(err)));
    } finally {
      setBusy(false);
    }
  }

  async function verify(value: string) {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ me: Me; needs_onboarding: boolean }>("/api/auth/verify", {
        body: { phone, code: value },
      });
      setMe(r.me);
      if (!r.needs_onboarding) navigate(next, { replace: true });
    } catch (err) {
      setCode("");
      const key = errorKey(err);
      const left = err instanceof ApiError ? err.body.attempts_left : undefined;
      setError(left !== undefined ? `${t(key)} (${left})` : t(key));
      if (
        err instanceof ApiError &&
        (err.body.error === "code_expired" || err.body.error === "too_many_attempts")
      )
        setStep("phone");
    } finally {
      setBusy(false);
    }
  }

  async function onboard(e: FormEvent) {
    e.preventDefault();
    if (!agreed || !me) return;
    setBusy(true);
    setError(null);
    try {
      const invite = storedInvite();
      const updated = await api<Me>("/api/auth/onboard", {
        body: {
          display_name: displayName,
          handle,
          terms_version: me.terms_current,
          locale,
          ...(invite ? { invite_code: invite } : {}),
        },
      });
      setMe(updated);
      navigate(next, { replace: true });
    } catch (err) {
      setError(t(errorKey(err)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page page--narrow">
      {!showOnboard && step === "phone" ? (
        <form className="form" onSubmit={sendCode}>
          <h1 className="h1">{t("auth.phone.title")}</h1>
          <p className="muted">{t("auth.phone.hint")}</p>
          <label className="field">
            <span className="label">{t("settings.phone")}</span>
            <input
              className="input"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="0901 234 567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              autoFocus
              data-testid="phone"
            />
          </label>
          {error ? (
            <p className="error" role="alert">
              {error}
            </p>
          ) : null}
          <button
            className="btn btn--accent btn--block"
            disabled={busy || !phone.trim()}
            type="submit"
          >
            {t("auth.phone.submit")}
          </button>
        </form>
      ) : null}

      {!showOnboard && step === "otp" ? (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (code.length === 6) void verify(code);
          }}
        >
          <h1 className="h1">{t("auth.otp.title")}</h1>
          <p className="muted">
            {t("auth.otp.sent_to", { phone: formatPhone(normalizePhone(phone) ?? phone) })}
          </p>
          <input
            ref={codeRef}
            className="input input--otp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            value={code}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, "").slice(0, 6);
              setCode(v);
              if (v.length === 6) void verify(v);
            }}
            aria-label={t("auth.otp.title")}
            disabled={busy}
            data-testid="otp"
          />
          {error ? (
            <p className="error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="row row--between">
            <button className="btn btn--ghost" type="button" onClick={() => setStep("phone")}>
              {t("auth.otp.change_phone")}
            </button>
            <button
              className="btn btn--ghost"
              type="button"
              disabled={resendIn > 0 || busy}
              onClick={() => void sendCode()}
            >
              {resendIn > 0 ? t("auth.otp.resend_in", { seconds: resendIn }) : t("auth.otp.resend")}
            </button>
          </div>
        </form>
      ) : null}

      {showOnboard ? (
        <form className="form" onSubmit={onboard}>
          <h1 className="h1">{t("auth.onboard.title")}</h1>
          <label className="field">
            <span className="label">{t("auth.onboard.display_name")}</span>
            <input
              className="input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              minLength={2}
              maxLength={30}
              required
              autoFocus
              data-testid="display-name"
            />
          </label>
          <label className="field">
            <span className="label">{t("auth.onboard.handle")}</span>
            <input
              className="input"
              value={handle}
              onChange={(e) =>
                setHandle(
                  e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9_]/g, "")
                    .slice(0, 20),
                )
              }
              minLength={3}
              maxLength={20}
              pattern="[a-z0-9_]{3,20}"
              required
              data-testid="handle"
            />
            <span className="hint">{t("auth.onboard.handle_hint", { handle: handle || "…" })}</span>
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              data-testid="terms"
            />
            <span>
              {t("auth.onboard.terms")}{" "}
              <Link to="/legal/terms" target="_blank">
                {t("auth.onboard.terms_link")}
              </Link>{" "}
              ·{" "}
              <Link to="/legal/prohibited" target="_blank">
                {t("auth.onboard.prohibited_link")}
              </Link>
            </span>
          </label>
          {error ? (
            <p className="error" role="alert">
              {error}
            </p>
          ) : null}
          <button
            className="btn btn--accent btn--block"
            disabled={busy || !agreed}
            type="submit"
            data-testid="onboard-submit"
          >
            {t("auth.onboard.submit")}
          </button>
        </form>
      ) : null}
    </div>
  );
}
